import invariant from 'invariant';

import type { I18nFn } from 'i18n/types/I18nFn';

export type AddressComponents = {
  street_number?: string;
  route?: string;
  locality?: string;
  sublocality_level_1?: string;
  postal_code?: string;
  administrative_area_level_1?: {
    long: string;
    short: string;
  };
  administrative_area_level_3?: string;
  administrative_area_level_2?: string;
  postal_town?: string;
  country?: {
    short: string;
    long: string;
  };
};

export type SerializedAddressComponent = {
  city?: string;
  state: string;
  stateCode: string;
  line1: string;
  geoType: string;
  country?: string;
  zipcode?: string;
  countryFull?: string;
  latLongAddress?: string;
  latitude?: number;
  longitude?: number;
};

function isAddressComponentsKey(key: string): key is keyof AddressComponents {
  return [
    'street_number',
    'route',
    'locality',
    'sublocality_level_1',
    'postal_code',
    'administrative_area_level_1',
    'administrative_area_level_3',
    'administrative_area_level_2',
    'postal_town',
    'country',
  ].includes(key);
}

export function getAddressComponents(
  addressComponents: google.maps.GeocoderAddressComponent[],
): AddressComponents {
  return addressComponents.reduce<Partial<AddressComponents>>(
    (hash, component) => {
      const [attributeName] = component.types;

      if (isAddressComponentsKey(attributeName)) {
        if (
          ['administrative_area_level_1', 'country'].includes(attributeName)
        ) {
          return {
            ...hash,
            [attributeName]: {
              long: component.long_name,
              short: component.short_name,
            },
          };
        }
        return {
          ...hash,
          [attributeName]: component.long_name,
        };
      }

      return hash;
    },
    {},
  ) as AddressComponents;
}

export function getCity(addressComponents: AddressComponents) {
  return (
    addressComponents.locality ??
    addressComponents.postal_town ??
    addressComponents.sublocality_level_1 ??
    addressComponents.administrative_area_level_3 ??
    addressComponents.administrative_area_level_2
  );
}

export function parseAddressComponents(
  i18n: I18nFn,
  geocoderAddressComponent: google.maps.GeocoderAddressComponent[],
) {
  const addressComponents = getAddressComponents(geocoderAddressComponent);
  const streetNumber = addressComponents.street_number;
  const { route } = addressComponents;
  const city = getCity(addressComponents);
  const administrativeArea = addressComponents.administrative_area_level_1 ?? {
    long: i18n('(no state)'),
    short: 'XA',
  };
  const state = administrativeArea.long;
  const stateCode = administrativeArea.short;
  const zipcode = addressComponents.postal_code;
  const countryFull = addressComponents.country?.long;
  const country = addressComponents.country?.short;
  const line1 = [];

  if (streetNumber) {
    line1.push(streetNumber);
  }

  if (route) {
    line1.push(route);
  }

  return {
    line1: line1.join(' '),
    city,
    state,
    stateCode,
    zipcode,
    country,
    countryFull,
  };
}

export function serializeAddress(
  i18n: I18nFn,
  place: google.maps.GeocoderResult,
): SerializedAddressComponent {
  invariant(place, 'GeocoderResult missing');
  const addressComponents = parseAddressComponents(
    i18n,
    place.address_components,
  );
  const { formatted_address: formattedAddress, geometry } = place;
  const { location } = geometry;
  const { line1, city, state, stateCode, zipcode, country } = addressComponents;
  const [geoType] = place.types;
  const latLongAddress = formattedAddress;
  const latitude = location ? location.lat() : undefined;
  const longitude = location ? location.lng() : undefined;
  return {
    line1,
    city,
    state,
    stateCode,
    zipcode,
    country,
    geoType,
    latLongAddress,
    latitude,
    longitude,
  };
}
